/**
 * A partner shop's or an event's own picture (owner, 2026-10-25: «show and allow to add image when adding partner
 * butikk», «in program when we add program then we need to add image»).
 *
 * Checked here with the store's own rules (Butikk readPicture: JPG, PNG or WebP; the shorter side at least 300 px; a
 * large phone photo made smaller first), and sent when the form is saved — a new shop or event has no id before that.
 * The server checks again, strips all metadata and stores its own JPEG.
 *
 * Banner studio (2026-10-31): an event's picture can also be one of the mosque's banners, drawn by the panel at the
 * screens' size («Velg et banner», lib/banner-attach). Such a pick carries the banner's id and title: the server
 * remembers where the picture came from, and the field says so.
 */
import React, { useState } from 'react';
import { send } from '../lib/api';
import { readPicture } from '../views/Butikk';
import { Btn, DropZone } from './ui';
import { useToastOn } from '../lib/hooks';

/** A picture chosen and not sent yet (`bannerId`: drawn from that banner); `remove` = take the current one away on save. */
export type Picked = { data: string; bannerId?: string; bannerTitle?: string } | { remove: true } | null;

/** Sends what was chosen to `/partners/:id/image` or `/events/:id/image`. → null when done, or what went wrong. */
export async function sendPicture(path: string, picked: Picked): Promise<string | null> {
  if (!picked) return null;
  const r = 'remove' in picked ? await send(path, { method: 'DELETE' })
    : await send(path, { method: 'PUT', body: JSON.stringify({ data: picked.data, ...(picked.bannerId ? { bannerId: picked.bannerId } : {}) }) });
  return r.ok ? null : (r.body.message_no || r.body.message || 'Bildet ble ikke lagret.');
}

export function PictureField({ label, note, current, currentFrom, picked, onPick, onChooseBanner }: {
  label: string; note: string; current?: string | null; picked: Picked; onPick: (p: Picked) => void;
  /** The banner the CURRENT picture was drawn from, when the server says so. */
  currentFrom?: string | null;
  /** «Velg et banner»: shown when the person may read the mosque's banners. */
  onChooseBanner?: () => void;
}) {
  const [error, setError] = useState('');
  useToastOn(error, 'err');
  const shown = picked && 'data' in picked ? picked.data : picked && 'remove' in picked ? null : current || null;
  const from = picked && 'data' in picked ? picked.bannerTitle || null : picked && 'remove' in picked ? null : currentFrom || null;
  return (
    <div className="field">
      <b>{label}</b>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 6 }}>
        {shown && <img src={shown} alt="" style={{ width: 200, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12, border: '1px solid var(--field-grey)' }} />}
        <div style={{ flex: 1, minWidth: 220 }}>
          <DropZone title={shown ? 'Bytt bilde' : 'Velg et bilde'} accept="image/jpeg,image/png,image/webp" onFile={async (file) => {
            setError('');
            const r = await readPicture(file);
            if (!r.ok) { setError(r.message); return; }
            onPick({ data: r.pic.data });
          }}>
            <span className="note">{note}</span>
          </DropZone>
          <div className="row" style={{ marginTop: 8, alignItems: 'center' }}>
            {onChooseBanner && <Btn ghost onClick={onChooseBanner}>{shown ? 'Bytt til et banner' : 'Velg et banner'}</Btn>}
            {shown && <Btn ghost onClick={() => onPick(current ? { remove: true } : null)}>Fjern bildet</Btn>}
            {from && <span className="note">Fra banneret «{from}»</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
